import { ReactNode, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

export interface ColumnDef<T> {
  key: string;
  label: string;
  sortable?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  render: (item: T) => ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  keyField: keyof T;
  loading?: boolean;
  emptyIcon?: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  totalCount?: number;
  serverPagination?: boolean;
  page?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (item: T) => void;
  expandedRow?: (item: T) => ReactNode;
  expandedId?: string | null;
  rowClassName?: (item: T) => string;
  rowTitle?: (item: T) => string;
}

function SortIcon({ columnKey, sortKey, sortDir }: { columnKey: string; sortKey?: string; sortDir?: 'asc' | 'desc' }) {
  if (sortKey !== columnKey) return <ArrowUpDown size={12} className="text-theme-secondary opacity-60" />;
  return sortDir === 'asc'
    ? <ArrowUp size={12} className="text-blue-600" />
    : <ArrowDown size={12} className="text-blue-600" />;
}

export function DataTable<T>({
  data,
  columns,
  keyField,
  loading,
  emptyIcon,
  emptyTitle = 'No data found',
  emptyDescription,
  sortKey,
  sortDir,
  onSort,
  totalCount: externalTotalCount,
  serverPagination,
  page: externalPage,
  onPageChange,
  pageSize: externalPageSize,
  onPageSizeChange,
  onRowClick,
  expandedRow,
  expandedId,
  rowClassName,
  rowTitle,
}: DataTableProps<T>) {
  const [internalPage, setInternalPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(25);

  const page = serverPagination ? (externalPage ?? 0) : internalPage;
  const pageSize = externalPageSize ?? internalPageSize;
  const totalCount = externalTotalCount ?? data.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const displayData = useMemo(() => {
    if (serverPagination) return data;
    const start = page * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, serverPagination]);

  const handlePageChange = (newPage: number) => {
    if (serverPagination && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    } else {
      setInternalPageSize(newSize);
    }
    handlePageChange(0);
  };

  const rangeStart = totalCount === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, totalCount);

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line flex flex-col min-h-0 flex-1 overflow-hidden">
      <div className="overflow-x-auto flex-1 min-h-0 flex flex-col">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-line bg-subtle">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2.5 text-left ${col.headerClassName || ''}`}
                >
                  {col.sortable && onSort ? (
                    <button
                      onClick={() => onSort(col.key)}
                      className="flex items-center gap-1 text-[11px] font-semibold text-theme-secondary uppercase tracking-wider hover:text-theme-primary transition-colors"
                    >
                      {col.label}
                      <SortIcon columnKey={col.key} sortKey={sortKey} sortDir={sortDir} />
                    </button>
                  ) : (
                    <span className="text-[11px] font-semibold text-theme-secondary uppercase tracking-wider">
                      {col.label}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-line divide-y">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center">
                  <Loader2 size={24} className="animate-spin text-theme-secondary mx-auto" />
                </td>
              </tr>
            ) : displayData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center">
                  {emptyIcon && <div className="mb-3 flex justify-center">{emptyIcon}</div>}
                  <p className="text-sm font-medium text-theme-primary">{emptyTitle}</p>
                  {emptyDescription && <p className="text-xs text-theme-secondary mt-1">{emptyDescription}</p>}
                </td>
              </tr>
            ) : (
              displayData.map((item) => {
                const key = String(item[keyField]);
                const isExpanded = expandedId === key;
                return (
                  <DataTableRow
                    key={key}
                    item={item}
                    columns={columns}
                    onRowClick={onRowClick}
                    isExpanded={isExpanded}
                    expandedRow={expandedRow}
                    rowClassName={rowClassName}
                    rowTitle={rowTitle}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-line bg-subtle flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-theme-secondary">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="text-xs border border-line bg-surface text-theme-primary rounded px-1.5 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-theme-secondary">
              {rangeStart}-{rangeEnd} of {totalCount}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(Math.max(0, page - 1))}
                disabled={page <= 0}
                className="p-1 rounded hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} className="text-theme-secondary" />
              </button>
              <button
                onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 rounded hover:bg-subtle disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} className="text-theme-secondary" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DataTableRow<T>({
  item,
  columns,
  onRowClick,
  isExpanded,
  expandedRow,
  rowClassName,
  rowTitle,
}: {
  item: T;
  columns: ColumnDef<T>[];
  onRowClick?: (item: T) => void;
  isExpanded: boolean;
  expandedRow?: (item: T) => ReactNode;
  rowClassName?: (item: T) => string;
  rowTitle?: (item: T) => string;
}) {
  return (
    <>
      <tr
        className={`hover:bg-subtle transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(item) || ''}`}
        onClick={() => onRowClick?.(item)}
        title={rowTitle?.(item)}
      >
        {columns.map((col) => (
          <td key={col.key} className={`px-3 py-2 ${col.cellClassName || ''}`}>
            {col.render(item)}
          </td>
        ))}
      </tr>
      {isExpanded && expandedRow && (
        <tr>
          <td colSpan={columns.length} className="px-3 pb-3">
            {expandedRow(item)}
          </td>
        </tr>
      )}
    </>
  );
}
